import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import moment from 'moment-timezone';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Header from '../components/marketing/header.js';
import consumer from "../channels/consumer";
import { toast } from 'react-toastify';
import useOAuthPopup from '../hooks/useOAuthPopup.js';
import SettingsModal from '../components/meta-ads/SettingsModal';
import EditAdModal from '../components/meta-ads/EditAdModal';
import CampaignReview from '../components/CampaignReview';
import GenerateAIModal from '../components/meta-ads/GenerateAIModal';
import ReportsList from '../components/meta-ads/ReportsList';
import GenerationQueuePanel from '../components/meta-ads/GenerationQueuePanel';

// Helper: format a Date/ISO in a specific IANA timezone as YYYY-MM-DD
const formatDateInTz = (dateLike, tz) => moment.tz(dateLike, tz || 'UTC').format('YYYY-MM-DD');
// Helper: convert an ISO/Date to datetime-local string in event timezone
const toLocalInputForTz = (dateLike, tz) => dateLike ? moment.tz(dateLike, tz || 'UTC').format('YYYY-MM-DDTHH:mm') : '';
// Helper: now in event timezone for input min
const nowLocalInputForTz = (tz) => moment.tz(new Date(), tz || 'UTC').format('YYYY-MM-DDTHH:mm');
// Helper: human-readable time in event timezone (e.g., 8:00 am)
const formatTimeInTz = (dateLike, tz) => dateLike ? moment.tz(dateLike, tz || 'UTC').format('h:mm a') : '';

// Helper: Parse geo location string to Facebook API format
const parseGeoLocation = (locationString) => {
  if (!locationString || locationString.trim() === '') {
    return { 'countries': ['US'] }; // Default to US
  }
  
  const trimmed = locationString.trim();
  
  // If it's just a country code (2 letters, uppercase)
  if (/^[A-Z]{2}$/.test(trimmed)) {
    return { 'countries': [trimmed] };
  }
  
  // If it contains comma, try to parse as "City, Region, Country" or "City, Country"
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map(p => p.trim());
    
    if (parts.length >= 3) {
      // Format: "City, Region, Country"
      const [city, region, country] = parts;
      return {
        'cities': [{
          'name': city,
          'region': region,
          'country': country || 'US'
        }]
      };
    } else if (parts.length === 2) {
      // Could be "City, Country" or "Region, Country"
      const [first, second] = parts;
      
      // If second part is 2 letters, it's likely a country code
      if (/^[A-Z]{2}$/.test(second)) {
        // Assume it's "City, Country" format
        return {
          'cities': [{
            'name': first,
            'country': second
          }]
        };
      } else {
        // Assume it's "City, Region" and default country to US
        return {
          'cities': [{
            'name': first,
            'region': second,
            'country': 'US'
          }]
        };
      }
    }
  }
  
  // If it's a single word, assume it's a city in US
  return {
    'cities': [{
      'name': trimmed,
      'country': 'US'
    }]
  };
};

const VideoThumbnail = ({ videoUrl, alt, className }) => {
  const [thumbnail, setThumbnail] = useState(null);

  useEffect(() => {
    if (!videoUrl) return;

    const video = document.createElement("video");
    video.src = videoUrl;
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "metadata";

    video.addEventListener("loadedmetadata", () => {
      // Go to the middle of the video
      video.currentTime = video.duration / 2;
    });

    video.addEventListener("seeked", () => {
      // Once it jumps to that time, capture the frame
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setThumbnail(canvas.toDataURL("image/png"));
    });

    return () => {
      video.removeAttribute("src");
      video.load();
    };
  }, [videoUrl]);

  if (!thumbnail) return null;

  return <img src={thumbnail} alt={alt} className={className} />;
};


// Connect Page Modal Component
const ConnectPageModal = ({ isOpen, onClose }) => {
  const reactRoot = document.getElementById('react-root');
  const facebookConnected = reactRoot?.getAttribute('facebook-connected') === 'true';
  // This gate dismisses itself once ad accounts exist — which used to be observable only after a
  // full page load. The shared popup script no longer alerts or redirects the opener, so the
  // answer has to be re-read here when the popup closes, and mirrored back onto #react-root for
  // the rest of the page (which still reads the attribute).
  const [hasAdAccounts, setHasAdAccounts] = useState(
    () => reactRoot?.getAttribute('has-ad-accounts') === 'true'
  );

  const refreshAdAccounts = () => {
    fetch('/api/facebook/ad_accounts_status', { headers: { Accept: 'application/json' } })
      .then(res => res.json())
      .then(data => {
        const connected = Array.isArray(data?.ad_accounts) && data.ad_accounts.length > 0;
        setHasAdAccounts(connected);
        try { reactRoot?.setAttribute('has-ad-accounts', String(connected)); } catch (_e) {}
      })
      .catch(() => {});
  };

  const openOAuthPopup = useOAuthPopup(refreshAdAccounts);
  const openMetaAdsPopup = () => openOAuthPopup('/facebook/oauth/authorize_meta_ads?popup=1', 'metaAdsAuth');

  // Only show modal if it's open AND ad accounts are not connected
  if (!isOpen || hasAdAccounts) {
    return null;
  }

  return (
    <div className="modal-overlay connect-page-modal" onClick={(e) => {
      // Prevent closing modal if ad accounts are not connected
      e.stopPropagation();
    }}>
      <div className="modal-container connect-page-container" onClick={(e) => e.stopPropagation()}>
        <div className="connect-page-header">
          <h2>Connect Facebook Ad Account</h2>
        </div>
        
        <div className="connect-page-content">
          <div className="platform-tiles">
            <div
              className="platform-tile facebook-tile connect-page-tile-clickable"
              onClick={openMetaAdsPopup}
            >
              <div className="platform-icon">
                <img
                  src="/assets/icons/facebook-icon.png"
                  alt="Facebook Icon"
                  width="40"
                  height="40"
                />
              </div>
              <h3>Connect Facebook Ad Account</h3>
              <p>Connect your Facebook Ad Account to start creating ads</p>
            </div>
          </div>

          {/* Skip option for testing/development */}
          <div className="connect-page-skip-wrap">
            <button type="button" onClick={onClose} className="connect-page-skip-btn">
              Skip for now (AI Draft only)
            </button>
            <p className="connect-page-skip-note">
              You can still generate AI campaigns without connecting Facebook
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status filter configuration
const STATUS_FILTERS = [
  { value: 'all', label: 'All Campaigns' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'drafted', label: 'Drafted' },
  { value: 'archived', label: 'Archived' }
];

const MetaAdsAIPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlug } = useParams();
  const rootElement = document.getElementById('react-root');

  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopedUrl = useCallback((url) => {
    if (!companySlug) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}company_slug=${companySlug}`;
  }, [companySlug]);
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const [postsData, setPostsData] = useState({})
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Dashboard state
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAIDraftOpen, setIsAIDraftOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  // Event-first navigation state
  const [viewMode, setViewMode] = useState('events'); // 'events' | 'campaigns'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showArchivedEvents, setShowArchivedEvents] = useState(false);

  // Admin-only: event token usage summary
  const [eventTokenSummary, setEventTokenSummary] = useState(null);
  // Queue/Reports modal
  const [queueModalOpen, setQueueModalOpen] = useState(false);

  // Campaigns from API (draft/synced) — shown even when company has no ad accounts
  const [campaignsFromApi, setCampaignsFromApi] = useState([]);

  // Loading flags — gate events view until both fetches complete to prevent pop-in
  const [adsLoaded, setAdsLoaded] = useState(false);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);

  // Meta Ads sync state
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  // Watchdog ref: if ActionCable disconnects mid-sync, the syncing state would
  // never clear without this safety timeout (90 seconds — covers normal sync
  // duration with ample headroom).
  const syncWatchdogRef = useRef(null);

  // Helper function to process and group posts - used consistently everywhere
  const processAndGroupPosts = (postsData) => {
    const processedData = {};
    
    // Iterate through all dates in the data
    Object.keys(postsData).forEach(dateKey => {
      const posts = postsData[dateKey];
      
      // Process each post
      posts.forEach(post => {
        // For Meta Ads, determine grouping date based on status and budget type
        // Use consistent logic everywhere
        let groupDate;
        const tz = (post?.event && post.event.timezone) || post?.timezone || 'UTC';
        // Check ad_set from post first, then fallback to creative's ad_set
        const adSet = post.ad_set || post.creative?.ad_set || {};
        
        // If published, always use published_at
        if (post.status === 'published' && post.published_at) {
          groupDate = new Date(post.published_at);
        }
        // For both daily and lifetime budget, use start_date for grouping
        else if (adSet.start_date) {
          groupDate = new Date(adSet.start_date);
        }
        // Fallback to scheduled_at if start_date is not available
        else if (post.scheduled_at) {
          groupDate = new Date(post.scheduled_at);
        }
        else if (post.created_at) {
          // Fallback to created_at
          groupDate = new Date(post.created_at);
        }
        else {
          // If no dates are available, use the current date key
          groupDate = new Date(dateKey);
        }
        
        // Format the date for grouping in the post's event timezone
        const formattedDate = formatDateInTz(groupDate, tz);
        // Attach display time in event timezone for card rendering
        // Priority: published_at > (daily_budget nil -> start_date) > (lifetime_budget nil -> scheduled_at)
        let displayBase;
        if (post.status === 'published' && post.published_at) {
          displayBase = post.published_at;
        } else if (adSet.daily_budget == null && adSet.start_date) {
          displayBase = adSet.start_date;
        } else if (adSet.lifetime_budget == null && post.scheduled_at) {
          displayBase = post.scheduled_at;
        } else {
          displayBase = post.scheduled_at || post.published_at || post.created_at || groupDate;
        }
        post.time = formatTimeInTz(displayBase, tz);
        // Ensure ad_set is available on post object for later use
        if (!post.ad_set && adSet.id) {
          post.ad_set = adSet;
        }
        
        // Initialize the array for this date if it doesn't exist
        if (!processedData[formattedDate]) {
          processedData[formattedDate] = [];
        }
        
        // Add the post to the appropriate date group
        processedData[formattedDate].push(post);
      });
    });
    
    return processedData;
  };

  useEffect(() => {
    if (rootElement) {
      const id = rootElement.getAttribute('company-id');
      setCompanyId(id);
      
      // Check admin status
      setIsAdmin(rootElement.getAttribute('is-admin') === 'true');

      // Check connection status and show connect modal if needed
      const hasAdAccounts = rootElement.getAttribute('has-ad-accounts') === 'true';
      
      // Show connect modal ONLY if ad accounts are not connected
      // Calendar view will always be shown regardless
      if (!hasAdAccounts) {
        setConnectModalOpen(true);
      }
      
      // Check if generation is in progress (with timeout protection)
      const generating = sessionStorage.getItem('metaAdsAI_generating') === 'true';
      const startTime = sessionStorage.getItem('metaAdsAI_generation_start');
      console.log('[MetaAdsAI] Page load state:', { generating, startTime, sessionStorageRaw: sessionStorage.getItem('metaAdsAI_generating') });

      if (generating) {
        // Auto-clear if generation has been "in progress" for more than 5 minutes (300000ms)
        // Real generations should complete within 2-3 minutes max
        const MAX_GENERATION_TIME = 5 * 60 * 1000; // 5 minutes
        const elapsed = startTime ? Date.now() - parseInt(startTime, 10) : MAX_GENERATION_TIME + 1;

        if (elapsed > MAX_GENERATION_TIME) {
          console.warn('[MetaAdsAI] Clearing stale generation state (exceeded 5 minute timeout)');
          sessionStorage.removeItem('metaAdsAI_generating');
          sessionStorage.removeItem('metaAdsAI_generation_start');
          sessionStorage.removeItem('metaAdsAI_report_id');
        } else {
          setIsGenerating(true);
        }
      }
    }
  }, []);

  // Fetch event-level token summary for admin users in campaigns view
  useEffect(() => {
    if (!isAdmin || viewMode !== 'campaigns' || !selectedEvent) {
      setEventTokenSummary(null);
      return;
    }

    fetch(scopedUrl(`/api/meta_ads_ai/reports/event_summary?event_id=${selectedEvent.id}`), {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.report_count > 0) {
          setEventTokenSummary(data);
        } else {
          setEventTokenSummary(null);
        }
      })
      .catch(() => setEventTokenSummary(null));
  }, [isAdmin, viewMode, selectedEvent]);

  useEffect(() => {
    fetch(scopedUrl("/facebook_ads?include_details=true"))
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        // Use the consistent processing function
        const processedData = processAndGroupPosts(data || {});
        setPostsData(processedData);
        setAdsLoaded(true);
      })
      .catch(error => {
        console.error("Error fetching posts:", error);
        // Set empty state on error to prevent crashes
        setPostsData({});
        setAdsLoaded(true);
        toast.error('Failed to load ads. Please refresh the page.');
      });
  }, []);

  // Fetch campaigns from API so draft campaigns are visible even when company has no ad accounts
  useEffect(() => {
    fetch(scopedUrl('/api/facebook/campaigns'), { headers: { Accept: 'application/json' } })
      .then(res => (res.ok ? res.json() : { campaigns: [] }))
      .then(data => {
        setCampaignsFromApi(data.campaigns || []);
        setCampaignsLoaded(true);
      })
      .catch(() => {
        setCampaignsFromApi([]);
        setCampaignsLoaded(true);
      });
  }, []);

  // Reusable helper to re-fetch campaigns from API after status changes
  const refreshCampaignsFromApi = () => {
    fetch(scopedUrl('/api/facebook/campaigns'), { headers: { Accept: 'application/json' } })
      .then(res => (res.ok ? res.json() : { campaigns: [] }))
      .then(data => setCampaignsFromApi(data.campaigns || []))
      .catch(() => {});
  };

  // Clears the syncing state and any pending watchdog. Called from terminal
  // ActionCable events (_complete, _error) and from the 90s watchdog itself.
  const clearSyncingState = () => {
    setSyncing(false);
    if (syncWatchdogRef.current) {
      clearTimeout(syncWatchdogRef.current);
      syncWatchdogRef.current = null;
    }
  };

  // Sync campaigns from Meta Ads Manager (async — see ActionCable handlers for progress/complete)
  const handleSyncCampaigns = async () => {
    setSyncing(true);
    // 90s safety: if ActionCable disconnects mid-sync, the button would otherwise
    // stay stuck on "Syncing..." forever. The terminal ActionCable handlers clear
    // this timer on success/error.
    if (syncWatchdogRef.current) clearTimeout(syncWatchdogRef.current);
    syncWatchdogRef.current = setTimeout(() => {
      setSyncing(false);
      toast.warning('Sync did not complete within 90s — refreshing data anyway');
      refreshCampaignsFromApi();
      syncWatchdogRef.current = null;
    }, 90_000);

    try {
      const res = await fetch(scopedUrl('/api/facebook/campaigns/sync'), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      const data = await res.json();
      // 202 Accepted means the job was enqueued — progress comes via ActionCable
      if (res.status === 202 && data.success) {
        toast.info(data.message);
        // setSyncing(false) happens when meta_ads_sync_complete or meta_ads_sync_error arrives
      } else {
        toast.error(data.error || 'Sync failed to start');
        clearSyncingState();
      }
    } catch (e) {
      toast.error('Failed to start sync');
      clearSyncingState();
    }
  };

  // ActionCable subscription for real-time updates
  useEffect(() => {
    if (!consumer) {
      return;
    }
    
    if (!companyId) {
      return;
    }
    
    const alertConfigurations = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
    };
    
    const subscription = consumer.subscriptions.create(
      { 
        channel: "CompanyPostsChannel", 
        company_id: companyId
      },
      {
        initialized() {
        },
        connected() {
          try {
            this.perform('test_connection');
          } catch (err) {
            console.error("[ActionCable] Error performing test_connection:", err);
          }
        },
        disconnected() {
          console.warn("[ActionCable] Disconnected from CompanyPostsChannel");
        },
        rejected() {
          console.error("[ActionCable] Subscription rejected - check Rails logs (server-side)");
        },
        received(data) {
          try {
            if (data?.action === "ads_created") {
              toast.success(data.message, alertConfigurations);
              // Hide progress header when ads are created
              setIsGenerating(false);
              sessionStorage.removeItem('metaAdsAI_generating');
              sessionStorage.removeItem('metaAdsAI_generation_start');
              sessionStorage.removeItem('metaAdsAI_report_id');
              // Refresh queue panel
              if (window.__generationQueueRefresh) {
                window.__generationQueueRefresh();
              }
            }

            // Handle AI generation progress updates
            if (data?.action === "ai_generation_progress") {
              toast.info(data.message, { ...alertConfigurations, autoClose: 3000 });
              // Trigger queue panel refresh for instant stage updates
              if (window.__generationQueueRefresh) {
                window.__generationQueueRefresh();
              }
            }

            if (data?.action === "error") {
              toast.error(data.message, alertConfigurations);
              // Hide progress header on error
              setIsGenerating(false);
              sessionStorage.removeItem('metaAdsAI_generating');
              sessionStorage.removeItem('metaAdsAI_generation_start');
              // Refresh queue panel
              if (window.__generationQueueRefresh) {
                window.__generationQueueRefresh();
              }
            }

            if (data?.action === "ads_data_created") {
              handleNewAdsData(data.ads);
              // Hide progress header when ads data is received
              setIsGenerating(false);
              sessionStorage.removeItem('metaAdsAI_generating');
              sessionStorage.removeItem('metaAdsAI_generation_start');
            }

            // Handle ad publishing progress messages
            if (data?.action === "ad_publish_progress") {
              toast.info(data.message, { ...alertConfigurations, autoClose: 3000 });
            }

            // Handle ad publishing errors
            if (data?.action === "ad_publish_error") {
              toast.error(data.message, alertConfigurations);
            }

            // Handle ad deletion progress messages
            if (data?.action === "ad_delete_progress") {
              toast.info(data.message, { ...alertConfigurations, autoClose: 3000 });
            }

            // Handle ad deletion errors
            if (data?.action === "ad_delete_error") {
              toast.error(data.message, alertConfigurations);
            }

            // Meta Ads sync — progress
            if (data?.action === "meta_ads_sync_progress") {
              toast.info(data.message, { ...alertConfigurations, autoClose: 4000 });
            }

            // Meta Ads sync — complete
            if (data?.action === "meta_ads_sync_complete") {
              toast.success(data.message, alertConfigurations);
              if (data.last_synced_at) {
                setLastSyncedAt(data.last_synced_at);
              }
              clearSyncingState();
              refreshCampaignsFromApi();
              // Also refresh ads list
              fetch(scopedUrl("/facebook_ads?include_details=true"))
                .then(res => (res.ok ? res.json() : {}))
                .then(adsData => {
                  const processedData = processAndGroupPosts(adsData || {});
                  setPostsData(processedData);
                })
                .catch(() => {});
            }

            // Meta Ads sync — error
            if (data?.action === "meta_ads_sync_error") {
              toast.error(data.message, alertConfigurations);
              clearSyncingState();
            }
          } catch (err) {
            console.error("[ActionCable] Error handling received data:", err);
          }
        }
      }
    );
    
    return () => {
      subscription?.unsubscribe();
    };
  }, [companyId]);

  const handleNewAdsData = (newAds) => {
    if (!newAds || newAds.length === 0) return;

    // Use functional state update to ensure we're working with the latest state
    setPostsData(prevPostsData => {
      // Create a completely new object structure
      const currentPosts = prevPostsData || {};
      
      // Process each new ad and create new date groups
      const newAdsByDate = {};
      
      newAds.forEach(ad => {
        // Check if this ad already exists in current state
        const adExists = Object.values(currentPosts).some(dateAds => 
          Array.isArray(dateAds) && dateAds.some(existingAd => 
            existingAd && existingAd.id === ad.id
          )
        );
        
        if (adExists) {
          return;
        }
        
        // Use the same grouping logic as processAndGroupPosts
        let groupDate;
        const tz = (ad?.event && ad.event.timezone) || ad?.timezone || 'UTC';
        // Check ad_set from ad first, then fallback to creative's ad_set
        const adSet = ad.ad_set || ad.creative?.ad_set || {};
        
        // If published, always use published_at
        if (ad.status === 'published' && ad.published_at) {
          groupDate = new Date(ad.published_at);
        }
        // For both daily and lifetime budget, use start_date for grouping
        else if (adSet.start_date) {
          groupDate = new Date(adSet.start_date);
        }
        // Fallback to scheduled_at if start_date is not available
        else if (ad.scheduled_at) {
          groupDate = new Date(ad.scheduled_at);
        }
        else if (ad.created_at) {
          groupDate = new Date(ad.created_at);
        }
        else {
          return; // Skip if no valid date
        }
        
        // Format the date for grouping in the ad's event timezone
        const formattedDate = formatDateInTz(groupDate, tz);
        
        // Attach display time in event timezone for card rendering
        // Priority: published_at > (daily_budget nil -> start_date) > (lifetime_budget nil -> scheduled_at)
        let displayBase;
        if (ad.status === 'published' && ad.published_at) {
          displayBase = ad.published_at;
        } else if (adSet.daily_budget == null && adSet.start_date) {
          displayBase = adSet.start_date;
        } else if (adSet.lifetime_budget == null && ad.scheduled_at) {
          displayBase = ad.scheduled_at;
        } else {
          displayBase = ad.scheduled_at || ad.published_at || ad.created_at || groupDate;
        }
        ad.time = formatTimeInTz(displayBase, tz);
        // Ensure ad_set is available on ad object for later use
        if (!ad.ad_set && adSet.id) {
          ad.ad_set = adSet;
        }
        
        // Initialize the array for this date if it doesn't exist
        if (!newAdsByDate[formattedDate]) {
          newAdsByDate[formattedDate] = [];
        }
        
        // Add the ad to the appropriate date group
        newAdsByDate[formattedDate].push(ad);
      });
      
      // Merge new ads with existing posts data
      const mergedData = { ...currentPosts };
      Object.keys(newAdsByDate).forEach(dateKey => {
        if (mergedData[dateKey]) {
          // Merge arrays, avoiding duplicates
          const existingIds = new Set(mergedData[dateKey].map(p => p.id));
          const newAdsForDate = newAdsByDate[dateKey].filter(ad => !existingIds.has(ad.id));
          mergedData[dateKey] = [...mergedData[dateKey], ...newAdsForDate];
        } else {
          mergedData[dateKey] = newAdsByDate[dateKey];
        }
      });
      
      return mergedData;
    });
  };

  const openEditModal = (post) => {
    setCurrentPost(post);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentPost(null);
  };

  const handleDeleteAd = (adId) => {
    if (!adId) return;

    fetch(scopedUrl(`/facebook_ads/${adId}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Progress messages are shown via ActionCable, so we don't need a separate success toast
        // Remove the deleted ad from local state immediately
        setPostsData(prevPostsData => {
          const updatedPostsData = {};
          Object.keys(prevPostsData).forEach(dateKey => {
            const filteredPosts = prevPostsData[dateKey].filter(post => post.id !== adId);
            if (filteredPosts.length > 0) {
              updatedPostsData[dateKey] = filteredPosts;
            }
          });
          return updatedPostsData;
        });
        
        // Optionally refresh from server to ensure consistency
        // Use the consistent processing function
        fetch(scopedUrl("/facebook_ads?include_details=true"))
          .then(res => res.json())
          .then(serverData => {
            // Use the consistent processing function
            const processedData = processAndGroupPosts(serverData);
            // Update state with fresh data from server
            setPostsData(processedData);
          })
          .catch(err => {
            console.error('Error fetching updated data after delete:', err);
            // Don't reload, just keep the local state update
          });
      } else {
        toast.error(`Failed to delete ad: ${data.error || 'Unknown error'}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      }
    })
    .catch(error => {
      console.error('Error deleting ad:', error);
      toast.error(`Error: ${error.message || 'Failed to delete ad'}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
    });
  };

  const savePostChanges = (updatedPost, action = "update", onComplete, shouldCloseModal = false) => {
    // Store reference to closeModal for use in callbacks
    const closeModalRef = closeModal;
    // Create a deep copy to avoid mutating the original state
    const updatedPostsData = {};
    Object.keys(postsData).forEach(dateKey => {
      updatedPostsData[dateKey] = postsData[dateKey].map(post => ({ ...post }));
    });
    
    // Process the datetime-local value
    let updatedPostWithTime = { ...updatedPost };
    
    // Determine which date to use for grouping based on budget type
    const tz = (updatedPost?.event && updatedPost.event.timezone) || updatedPost?.timezone || 'UTC';
    
    // Find the original post to check its ad_set
    let originalPost = null;
    Object.keys(updatedPostsData).forEach(dateKey => {
      const foundPost = updatedPostsData[dateKey].find(p => p.id === updatedPost.id);
      if (foundPost) {
        originalPost = foundPost;
      }
    });
    
    // Check ad_set from original post first, then updatedPost, then creative's ad_set
    const adSet = originalPost?.ad_set || originalPost?.creative?.ad_set || updatedPost.ad_set || updatedPost.creative?.ad_set || {};
    let groupDate;
    let shouldRegroup = false;
    
    // If published, always use published_at
    if (updatedPost.status === 'published') {
      if (updatedPost.published_at) {
        groupDate = new Date(updatedPost.published_at);
      } else {
        // If no published_at exists, set it to current time
        const now = new Date();
        updatedPostWithTime.published_at = now.toISOString();
        groupDate = now;
      }
      updatedPostWithTime.time = formatTimeInTz(groupDate, tz);
      shouldRegroup = true;
    }
    // For both daily and lifetime budget, use startDate for grouping
    else if (updatedPost.startDate) {
      let startMoment;
      if (updatedPost.startDate.includes('T')) {
        startMoment = moment.tz(updatedPost.startDate, 'YYYY-MM-DDTHH:mm', tz);
      } else {
        startMoment = moment.tz(`${updatedPost.startDate} 00:00`, 'YYYY-MM-DD HH:mm', tz);
      }
      groupDate = startMoment.toDate();
      updatedPostWithTime.time = startMoment.format('h:mm a');
      shouldRegroup = true;
    }
    // Fallback to scheduledDate if startDate is not available
    else if (updatedPost.scheduledDate) {
      let scheduledMoment;
      if (updatedPost.scheduledDate.includes('T')) {
        scheduledMoment = moment.tz(updatedPost.scheduledDate, 'YYYY-MM-DDTHH:mm', tz);
      } else {
        scheduledMoment = moment.tz(`${updatedPost.scheduledDate} 00:00`, 'YYYY-MM-DD HH:mm', tz);
      }
      groupDate = scheduledMoment.toDate();
      updatedPostWithTime.time = scheduledMoment.format('h:mm a');
      shouldRegroup = true;
    }
    // Fallback to scheduledDateTime if available
    else if (updatedPost.scheduledDateTime) {
      // Extract time in the format needed for display (e.g., "8:00 am")
      // scheduledDateTime is in event timezone; convert to UTC ISO for server using moment
      const localMoment = moment.tz(updatedPost.scheduledDateTime, 'YYYY-MM-DDTHH:mm', tz);
      const utcInstant = localMoment.clone().utc();
      updatedPostWithTime.time = localMoment.format('h:mm a');
      updatedPostWithTime.date = utcInstant.toISOString();
      updatedPostWithTime.scheduled_at = utcInstant.toISOString();
      
      // Use the scheduled date for grouping if post is draft or scheduled
      if (updatedPost.status === 'draft' || updatedPost.status === 'scheduled') {
        groupDate = localMoment.toDate();
        shouldRegroup = true;
      }
    }
    // If post is rejected or draft without scheduled_at/startDate, use created_at
    else if ((updatedPost.status === 'rejected' || updatedPost.status === 'draft') && !shouldRegroup && updatedPost.created_at) {
      groupDate = new Date(updatedPost.created_at);
      shouldRegroup = true;
    }
    
    // Find the current date key that contains this post
    let currentDateKey = null;
    Object.keys(updatedPostsData).forEach(dateKey => {
      if (updatedPostsData[dateKey].some(p => p.id === updatedPost.id)) {
        currentDateKey = dateKey;
      }
    });
    
    // If we need to regroup the post
    if (shouldRegroup && groupDate && currentDateKey) {
      // Use formatDateInTz to format date in event timezone
      const newDateKey = formatDateInTz(groupDate, tz);
      
      // If the post needs to move to a different date
      if (currentDateKey !== newDateKey) {
        // Remove from current date
        updatedPostsData[currentDateKey] = updatedPostsData[currentDateKey].filter(
          p => p.id !== updatedPost.id
        );
        
        // Add to new date
        if (!updatedPostsData[newDateKey]) {
          updatedPostsData[newDateKey] = [];
        }
        updatedPostsData[newDateKey].push(updatedPostWithTime);
      } else {
        // Just update in place
        const postIndex = updatedPostsData[currentDateKey].findIndex(p => p.id === updatedPost.id);
        if (postIndex !== -1) {
          updatedPostsData[currentDateKey][postIndex] = updatedPostWithTime;
        }
      }
    } else {
      // If no regrouping needed, just update the post in its current location
      if (currentDateKey) {
        const postIndex = updatedPostsData[currentDateKey].findIndex(p => p.id === updatedPost.id);
        if (postIndex !== -1) {
          updatedPostsData[currentDateKey][postIndex] = updatedPostWithTime;
        }
      }
    }
    
    // Prepare data for API call - map to Facebook Ads API structure
    // (tz is already declared above)
    
    // Process start_date and end_date for both daily and lifetime budget (datetime-local format)
    let startDate = null;
    let endDate = null;
    if (updatedPost.startDate) {
      // Handle both date and datetime-local formats
      if (updatedPost.startDate.includes('T')) {
        startDate = moment.tz(updatedPost.startDate, 'YYYY-MM-DDTHH:mm', tz).utc().toISOString();
      } else {
        startDate = moment.tz(`${updatedPost.startDate} 00:00`, 'YYYY-MM-DD HH:mm', tz).utc().toISOString();
      }
    }
    if (updatedPost.endDate) {
      // Handle both date and datetime-local formats
      if (updatedPost.endDate.includes('T')) {
        endDate = moment.tz(updatedPost.endDate, 'YYYY-MM-DDTHH:mm', tz).utc().toISOString();
      } else {
        endDate = moment.tz(`${updatedPost.endDate} 23:59`, 'YYYY-MM-DD HH:mm', tz).utc().toISOString();
      }
    }
    
    // Update ad_set structure in updatedPostWithTime for runtime display
    if (!updatedPostWithTime.ad_set) {
      updatedPostWithTime.ad_set = updatedPost.ad_set ? { ...updatedPost.ad_set } : {};
    }
    if (startDate) {
      updatedPostWithTime.ad_set.start_date = startDate;
    }
    if (endDate) {
      updatedPostWithTime.ad_set.end_date = endDate;
    }
    if (updatedPost.budgetStrategy) {
      updatedPostWithTime.ad_set.budget_strategy = updatedPost.budgetStrategy;
    }
    
    // Process scheduled date (datetime-local format) - can be used as fallback
    let scheduledAt = null;
    if (updatedPost.scheduledDate) {
      // Handle both date and datetime-local formats
      if (updatedPost.scheduledDate.includes('T')) {
        scheduledAt = moment.tz(updatedPost.scheduledDate, 'YYYY-MM-DDTHH:mm', tz).utc().toISOString();
      } else {
        scheduledAt = moment.tz(`${updatedPost.scheduledDate} 00:00`, 'YYYY-MM-DD HH:mm', tz).utc().toISOString();
      }
      // Update scheduled_at in updatedPostWithTime for runtime display
      updatedPostWithTime.scheduled_at = scheduledAt;
    } else if (updatedPost.scheduledDateTime) {
      scheduledAt = moment.tz(updatedPost.scheduledDateTime, 'YYYY-MM-DDTHH:mm', tz).utc().toISOString();
      updatedPostWithTime.scheduled_at = scheduledAt;
    }
    
    const adData = {
      id: updatedPost.id,
      // Campaign
      campaign_id: updatedPost.campaignId || "abc",
      // Page
      page_id: updatedPost.pageId || "ad",
      // Ad Set
      ad_set: {
        name: updatedPost.adSetName || '',
        budget_strategy: updatedPost.budgetStrategy || 'daily_budget',
        daily_budget: updatedPost.budgetStrategy === 'daily_budget' ? parseInt(updatedPost.dailyBudget) || null : null,
        lifetime_budget: updatedPost.budgetStrategy === 'lifetime_budget' ? parseInt(updatedPost.lifetimeBudget) || null : null,
        start_date: startDate,
        end_date: endDate,
        billing_event: updatedPost.billingEvent || 'IMPRESSIONS',
        optimization_goal: updatedPost.optimizationGoal || 'LINK_CLICKS',
        bid_strategy: updatedPost.bidStrategy || 'LOWEST_COST_WITHOUT_CAP',
        targeting: {
          age_min: parseInt(updatedPost.ageMin) || null,
          age_max: parseInt(updatedPost.ageMax) || null,
          geo_locations: parseGeoLocation(updatedPost.geoLocation || 'US')
        }
      },
      // Creative
      creative: {
        name: updatedPost.creativeName || '',
        message: updatedPost.creativeMessage || '',
        headline: updatedPost.creativeHeadline || '',
        description: updatedPost.creativeDescription || '',
        link: updatedPost.creativeLink || ''
      },
      // Ad
      name: updatedPost.adName || '',
      message: updatedPost.creativeMessage || updatedPost.description || '',
      description: updatedPost.creativeDescription || updatedPost.description || '',
      headline: updatedPost.creativeHeadline || updatedPost.title || '',
      status: updatedPostWithTime.status,
      scheduled_at: scheduledAt
    };

    // Update state immediately for runtime display
    setPostsData(updatedPostsData);
    
    const endpoint = action === "schedule"
      ? scopedUrl(`/facebook_ads/${updatedPost.id}/schedule_and_approve`)
      : scopedUrl(`/facebook_ads/${updatedPost.id}`);

    const method = action === "schedule" ? "POST" : "PATCH";

    fetch(endpoint, {
      method,
      headers: { 
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(adData)
    })
    .then(response => {
      // Check if response is ok (status 200-299)
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || 'Failed to update ad');
        });
      }
      return response.json();
    })
    .then(data => {
       if (data.success) {
        //Show success message
        if (action === "schedule") {
          // Check if ad was actually published (has ad_id and status is published)
          const adStatus = data.ad?.status || data.ad?.facebook_ad?.status;
          const adId = data.ad?.ad_id || data.ad?.facebook_ad?.ad_id;
          
          // If successfully published, reload the page to show "POSTED" status
          if (adStatus === 'published' && adId) {
            // Close the modal first
            if (shouldCloseModal || action === "schedule") {
              if (typeof closeModalRef === 'function') {
                closeModalRef();
              }
            }
            if (onComplete) onComplete();
            
            // Reload to show the correct "POSTED" status
            window.location.reload();
            return;
          }
          
          // If still scheduled (not yet published), update status to "scheduled" (APPROVED)
          // Also include facebook_review_status and other fields from response
          setPostsData(prevPostsData => {
            const updatedPostsData = {};
            Object.keys(prevPostsData).forEach(dateKey => {
              updatedPostsData[dateKey] = prevPostsData[dateKey].map(post => {
                if (post.id === updatedPost.id) {
                  return {
                    ...post,
                    status: 'scheduled',
                    facebook_review_status: data.ad?.facebook_review_status || post.facebook_review_status,
                    facebook_rejection_reason: data.ad?.facebook_rejection_reason || post.facebook_rejection_reason,
                    facebook_delivery_status: data.ad?.facebook_delivery_status || post.facebook_delivery_status,
                    updated_at: data.ad?.updated_at || post.updated_at
                  };
                }
                return post;
              });
            });
            return updatedPostsData;
          });
          
          // Close the modal and reset loading state
          if (onComplete) onComplete();
          
          // Close the modal if this is a schedule action
          // Use setTimeout to ensure state updates complete before closing modal
          if (shouldCloseModal || action === "schedule") {
            setTimeout(() => {
              if (typeof closeModalRef === 'function') {
                closeModalRef();
              }
            }, 100);
          }
          
          // Publishing progress will be shown via ActionCable
          // If publishing succeeds later, ActionCable will update the status
        } else {
          // For update action, refresh the data from server to get latest state
          fetch(scopedUrl("/facebook_ads?include_details=true"))
            .then(res => res.json())
            .then(serverData => {
              // Use the consistent processing function
              const processedData = processAndGroupPosts(serverData);
              
              // Update state with fresh data from server
              setPostsData(processedData);
              
              // Show success message
              toast.success('Ad updated successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
              });
              
              // Reset loading state before closing modal
              if (onComplete) onComplete();
              closeModal();
            })
            .catch(err => {
              console.error('Error fetching updated data:', err);
              // Still show success and close modal even if refresh fails
              toast.success('Ad updated successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
              });
              // Reset loading state before closing modal
              if (onComplete) onComplete();
              closeModal();
            });
        }
      } else {
        // Show error message if the API returned an error
        // Error messages are shown via ActionCable, but show a general error if needed
        const errorMsg = action === "schedule" 
          ? `Failed to schedule ad: ${data.error || 'Unknown error'}`
          : `Failed to update ad: ${data.error || 'Unknown error'}`;
        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
        
        // If schedule action failed, revert status back to draft
        if (action === "schedule") {
          setPostsData(prevPostsData => {
            const updatedPostsData = {};
            Object.keys(prevPostsData).forEach(dateKey => {
              updatedPostsData[dateKey] = prevPostsData[dateKey].map(post => {
                if (post.id === updatedPost.id) {
                  return {
                    ...post,
                    status: 'draft' // Revert to draft on error
                  };
                }
                return post;
              });
            });
            return updatedPostsData;
          });
        }
        
        // Reset loading state on error
        if (onComplete) onComplete();
      }
    })
    .catch(error => {
      console.error('Error updating post:', error);
      // Error messages are shown via ActionCable, but show a general error if needed
      toast.error(`Error: ${error.message || 'Failed to update ad'}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      
      // If schedule action failed, revert status back to draft
      if (action === "schedule") {
        setPostsData(prevPostsData => {
          const updatedPostsData = {};
          Object.keys(prevPostsData).forEach(dateKey => {
            updatedPostsData[dateKey] = prevPostsData[dateKey].map(post => {
              if (post.id === updatedPost.id) {
                return {
                  ...post,
                  status: 'draft' // Revert to draft on error
                };
              }
              return post;
            });
          });
          return updatedPostsData;
        });
      }
      
      // Reset loading state on error
      if (onComplete) onComplete();
    });
  };

  const getWeekDates = (date) => {
    const startOfWeek = new Date(date)
    startOfWeek.setDate(date.getDate() - date.getDay() + 1) // Start from Monday
    
    const dates = []
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek)
      currentDate.setDate(startOfWeek.getDate() + i)
      dates.push(currentDate)
    }
    return dates
  }

  const formatDate = (date) => {
    return date.toISOString().split('T')[0]
  }

  const formatDisplayDate = (date) => {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
    const dayName = days[date.getDay()]
    const dayNumber = date.getDate().toString().padStart(2, '0')
    return `${dayName} - ${dayNumber}`
  }

  const getWeekRange = (date) => {
    const dates = getWeekDates(date)
    const startDate = dates[0]
    const endDate = dates[6]
    
    const startMonth = startDate.toLocaleString('default', { month: 'long' })
    const startDay = startDate.getDate()
    const endMonth = endDate.toLocaleString('default', { month: 'short' })
    const endDay = endDate.getDate()
    
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startDate.getFullYear()}`
  }

  const weekDates = getWeekDates(currentWeek)

  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek)
    newWeek.setDate(currentWeek.getDate() + (direction * 7))
    setCurrentWeek(newWeek)
  }

  const getPlatformIcon = (platform) => {
      if (platform === 'facebook') {
        return (
          <img 
          src="/assets/icons/facebook-icon.png" 
          alt="Facebook Icon" 
          width="16" 
          height="16" 
          />
        )
      } else if (platform === 'instagram') {
        return (
          <img 
          src="/assets/icons/insta-icon.png" 
          alt="Facebook Icon" 
          width="16" 
          height="16" 
          />
        )
      }
    }


  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return '#10B981'
      case 'scheduled': return '#F59E0B'
      case 'draft': return '#6B7280'
      default: return '#6B7280'
    }
  }

  // Helper: Convert ads data + campaigns API into unified campaigns format for dashboard.
  // Campaigns from API are shown even when company has no ad accounts (draft-only flow).
  const convertAdsToCampaigns = useMemo(() => {
    const campaignsMap = {};

    // 1) Add all campaigns from API (drafts/synced) so they show with or without ad accounts
    (campaignsFromApi || []).forEach(c => {
      const key = String(c.id);
      const status = (c.status || 'DRAFT').toLowerCase();
      campaignsMap[key] = {
        id: c.id,
        campaign_name: c.name || 'Campaign',
        // Keep null when the campaign has no event yet — the campaignsByEvent grouping
        // uses 'unknown' only as a final UI fallback, not as a stored field. Storing
        // 'unknown' here would make it truthy and prevent the merge step (below) from
        // upgrading event metadata when an associated ad has a valid event.
        display_event_name: c.event_name || null,
        event_id: c.event_id || null,
        // Promotes several events (e.g. a CBO with an ad set per show) — never pin it to one.
        multi_event: !!c.multi_event,
        status,
        created_at: c.created_at,
        scheduled_start_at: c.start_date,
        scheduled_end_at: c.end_date,
        objective: c.objective,
        ads: [],
        _campaign_db_id: c.id,
        fullResponse: c.fullResponse
      };
    });

    // 2) Merge in ads from postsData when available (group by campaign, fill ads)
    if (postsData && typeof postsData === 'object') {
      const allAds = [];
      try {
        Object.values(postsData).forEach(dateAds => {
          if (Array.isArray(dateAds)) allAds.push(...dateAds);
        });
      } catch (e) {
        console.error('Error processing postsData:', e);
      }

      allAds.forEach(ad => {
        if (!ad) return;
        const dbId = ad.facebook_campaign_db_id != null ? String(ad.facebook_campaign_db_id) : null;
        const campaignId = ad.campaign_id || ad.facebook_campaign_id || `event_${ad.event?.id || ad.event_id || 'unknown'}`;
        const key = dbId || campaignId;
        const eventId = ad.event?.id || ad.event_id || null;
        const eventName = ad.event?.name || ad.event_title || null;

        if (campaignsMap[key]) {
          campaignsMap[key].ads.push(ad);
          // Upgrade event metadata if campaign didn't have it but the ad does
          if (!campaignsMap[key].event_id && !campaignsMap[key].multi_event && eventId) {
            campaignsMap[key].event_id = eventId;
            campaignsMap[key].display_event_name = eventName;
          }
        } else {
          const rawStatus = ad.campaign_status || ad.status || 'draft';
          const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : 'draft';
          campaignsMap[key] = {
            id: campaignId,
            campaign_name: ad.campaign_name || `${eventName || 'Untitled'} Campaign`,
            display_event_name: eventName,
            event_id: eventId,
            status,
            created_at: ad.created_at,
            scheduled_start_at: ad.ad_set?.start_date || ad.scheduled_at,
            scheduled_end_at: ad.ad_set?.end_date,
            objective: ad.objective,
            ads: [ad],
            _campaign_db_id: ad.facebook_campaign_db_id || null
          };
        }
      });
    }

    return Object.values(campaignsMap);
  }, [postsData, campaignsFromApi]);

  // Filter campaigns by status
  const filteredCampaigns = useMemo(() => {
    if (statusFilter === 'all') return convertAdsToCampaigns;

    return convertAdsToCampaigns.filter(c => {
      switch (statusFilter) {
        case 'active':
          return c.status === 'active';
        case 'paused':
          return c.status === 'paused';
        case 'drafted':
          return c.status === 'draft';
        case 'archived':
          return c.status === 'archived';
        default:
          return true;
      }
    });
  }, [convertAdsToCampaigns, statusFilter]);

  // Group campaigns by event for event-first navigation. Campaigns without an
  // event_id — evergreen ones (e.g., the Dinner Detective recurring shows
  // pattern) and multi-event ones (one CBO with an ad set per show) — share one
  // bucket, since neither belongs to a single event.
  const COMPANY_LEVEL_BUCKET_ID = '__company_level__';
  const campaignsByEvent = useMemo(() => {
    const grouped = {};
    convertAdsToCampaigns.forEach(campaign => {
      // Extract event info from campaign
      const resolvedEventId = campaign.multi_event
        ? null
        : (campaign.event_id || campaign.ads?.[0]?.event?.id || campaign.ads?.[0]?.event_id || null);
      const eventId = resolvedEventId || COMPANY_LEVEL_BUCKET_ID;
      const eventName = resolvedEventId
        ? (campaign.display_event_name || 'Untitled Event')
        : 'Multi-event & evergreen campaigns';

      if (!grouped[eventId]) {
        grouped[eventId] = {
          id: eventId,
          name: eventName,
          isCompanyLevel: eventId === COMPANY_LEVEL_BUCKET_ID,
          subtitle: eventId === COMPANY_LEVEL_BUCKET_ID ? 'Covers several events, or none in particular' : null,
          campaigns: [],
          isArchived: false
        };
      }
      grouped[eventId].campaigns.push(campaign);
    });

    // Mark events as archived if ALL their campaigns are archived
    Object.values(grouped).forEach(event => {
      event.isArchived = event.campaigns.every(c => c.status === 'archived');

      // Calculate status counts for rich event cards (show actual status: active vs paused)
      event.activeCampaigns = event.campaigns.filter(c => c.status === 'active').length;
      event.pausedCampaigns = event.campaigns.filter(c => c.status === 'paused').length;
      event.scheduledCampaigns = event.campaigns.filter(c => c.status === 'scheduled').length;
      event.publishedCampaigns = event.campaigns.filter(c => c.status === 'published').length;
      event.draftCampaigns = event.campaigns.filter(c => c.status === 'draft').length;
      event.archivedCampaigns = event.campaigns.filter(c => c.status === 'archived').length;
    });

    return Object.values(grouped);
  }, [convertAdsToCampaigns]);

  // Split events into active and archived
  const activeEvents = useMemo(() =>
    campaignsByEvent.filter(e => !e.isArchived),
    [campaignsByEvent]
  );

  const archivedEvents = useMemo(() =>
    campaignsByEvent.filter(e => e.isArchived),
    [campaignsByEvent]
  );

  // Restore campaigns view when navigating back from Campaign Detail (state: selectedEventId + viewMode)
  useEffect(() => {
    const state = location.state;
    if (state?.selectedEventId != null && campaignsByEvent.length > 0) {
      const event = campaignsByEvent.find(e => String(e.id) === String(state.selectedEventId));
      if (event) {
        setSelectedEvent(event);
        setViewMode('campaigns');
      }
    }
  }, [location.state, campaignsByEvent]);

  // Get campaigns for the selected event, filtered by status
  const campaignsForSelectedEvent = useMemo(() => {
    if (!selectedEvent) return [];

    const eventCampaigns = selectedEvent.campaigns || [];

    if (statusFilter === 'all') return eventCampaigns;

    return eventCampaigns.filter(c => {
      switch (statusFilter) {
        case 'active':
          return c.status === 'active';
        case 'paused':
          return c.status === 'paused';
        case 'drafted':
          return c.status === 'draft';
        case 'archived':
          return c.status === 'archived';
        default:
          return true;
      }
    });
  }, [selectedEvent, statusFilter]);

  // Status counts for selected event (for badges on tabs)
  const statusCounts = useMemo(() => {
    const campaigns = selectedEvent?.campaigns || [];
    return {
      all: campaigns.length,
      active: campaigns.filter(c => c.status === 'active').length,
      paused: campaigns.filter(c => c.status === 'paused').length,
      drafted: campaigns.filter(c => c.status === 'draft').length,
      archived: campaigns.filter(c => c.status === 'archived').length
    };
  }, [selectedEvent]);

  // Dashboard handlers
  const handleFilterStatus = (status) => {
    setStatusFilter(status);
  };

  const handleOpenAIDraft = () => {
    setIsAIDraftOpen(true);
  };

  const handleCloseAIDraft = () => {
    setIsAIDraftOpen(false);
  };

  const handleAIDraftSuccess = async (data) => {
    // For AI generation flow, the report is being generated in background
    // ActionCable will notify us when it's complete
    if (data?.report_id) {
      toast.info('AI generation started! This may take 1-2 minutes.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      return;
    }

    // Legacy flow for campaigns with fullResponse
    try {
      if (data?.fullResponse || data?.ai_explanations?._full_response?.value) {
        const fullResponse = data?.fullResponse || data?.ai_explanations?._full_response?.value;
        const eventId = data?.event_id || data?.event_data?.event_id || fullResponse?.event_data?.event_id || null;

        const response = await fetch(scopedUrl('/api/facebook/campaigns/populate_from_payload'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({
            payload: fullResponse,
            event_id: eventId
          })
        });

        const responseData = await response.json();

        if (response.ok && responseData.success) {
          toast.success('Campaign created successfully!');

          // Refresh ads data
          fetch(scopedUrl("/facebook_ads?include_details=true"))
            .then(res => res.json())
            .then(serverData => {
              const processedData = processAndGroupPosts(serverData);
              setPostsData(processedData);
            })
            .catch(err => console.error('Error fetching ads:', err));

          if (responseData.campaign?.id) {
            setSelectedCampaign({
              ...data,
              id: responseData.campaign.id,
              fullResponse: fullResponse
            });
            setIsReviewOpen(true);
          }
        } else {
          toast.error(responseData.error || responseData.errors?.join(', ') || 'Failed to create campaign');
        }
      } else if (data?.id) {
        // Refresh and open review if data already has ID
        fetch(scopedUrl("/facebook_ads?include_details=true"))
          .then(res => res.json())
          .then(serverData => {
            const processedData = processAndGroupPosts(serverData);
            setPostsData(processedData);
          })
          .catch(err => console.error('Error fetching ads:', err));

        setSelectedCampaign(data);
        setIsReviewOpen(true);
      }
    } catch (error) {
      toast.error('Error creating campaign: ' + error.message);
      console.error('Create campaign error:', error);
    }
  };

  const handleEditCampaign = async (campaign) => {
    const campaignId = campaign._campaign_db_id ?? campaign.id;
    const isDbId = campaignId != null && String(campaignId) !== '' && !String(campaignId).startsWith('event_');

    if (isDbId) {
      navigate(`/marketing/meta_ads_ai/companies/${companySlug}/campaigns/${campaignId}`);
      return;
    }

    // No DB id (e.g. event_xxx): open review modal
    try {
      const response = await fetch(scopedUrl(`/api/facebook/campaigns/${campaign._campaign_db_id || campaign.id}`), {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const campaignData = await response.json();
        setSelectedCampaign(campaignData);
        setIsReviewOpen(true);
      } else {
        setSelectedCampaign(campaign);
        setIsReviewOpen(true);
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      setSelectedCampaign(campaign);
      setIsReviewOpen(true);
    }
  };

  const handleCloseReview = () => {
    setIsReviewOpen(false);
    setSelectedCampaign(null);
  };

  const handleApproveCampaign = async (campaign) => {
    const campaignObj = typeof campaign === 'object' ? campaign : { id: campaign };
    const campaignName = campaignObj.campaign_name || campaignObj.name || 'this campaign';
    const confirmed = window.confirm(
      `Approve "${campaignName}" and schedule all ads? This will push the campaign to Meta Ads Manager.`
    );
    if (!confirmed) return;

    await handleSaveCampaign(campaignObj, 'schedule');
  };

  // Update campaign status via API
  const updateCampaignStatus = async (campaign, newStatus) => {
    const campaignId = campaign._campaign_db_id || campaign.id;
    if (!campaignId) {
      toast.error('Campaign ID not found');
      return false;
    }

    try {
      const response = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return true;
      } else {
        toast.error(data.error || `Failed to update campaign status`);
        return false;
      }
    } catch (error) {
      console.error('Error updating campaign status:', error);
      toast.error(`Error: ${error.message || 'Failed to update campaign'}`);
      return false;
    }
  };

  const handleRejectCampaign = async (campaign) => {
    const campaignName = campaign.campaign_name || 'this campaign';
    const confirmed = window.confirm(
      `Reject "${campaignName}"? This campaign will be archived and moved to the Archived tab.`
    );

    if (!confirmed) return;

    const success = await updateCampaignStatus(campaign, 'archived');
    if (success) {
      toast.success(`Campaign "${campaignName}" has been rejected and archived`);
      // Refresh both data sources so UI updates immediately
      fetch(scopedUrl("/facebook_ads?include_details=true"))
        .then(res => res.json())
        .then(serverData => {
          const processedData = processAndGroupPosts(serverData);
          setPostsData(processedData);
        })
        .catch(err => console.error('Error fetching ads:', err));
      refreshCampaignsFromApi();
    }
    handleCloseReview();
  };

  const handleArchiveCampaign = async (campaign) => {
    const campaignName = campaign.campaign_name || 'this campaign';
    const confirmed = window.confirm(
      `Archive "${campaignName}"? This campaign will be moved to the Archived tab.`
    );

    if (!confirmed) return;

    const success = await updateCampaignStatus(campaign, 'archived');
    if (success) {
      toast.success(`Campaign "${campaignName}" has been archived`);
      // Refresh both data sources so UI updates immediately
      fetch(scopedUrl("/facebook_ads?include_details=true"))
        .then(res => res.json())
        .then(serverData => {
          const processedData = processAndGroupPosts(serverData);
          setPostsData(processedData);
        })
        .catch(err => console.error('Error fetching ads:', err));
      refreshCampaignsFromApi();
    }
    handleCloseReview();
  };

  // Archive entire event (archives all campaigns within)
  const handleArchiveEvent = async (event) => {
    const confirmed = window.confirm(
      `Archive "${event.name}"? This will archive all ${event.campaigns.length} campaigns for this event.`
    );

    if (!confirmed) return;

    try {
      // Archive all campaigns for this event
      const promises = event.campaigns.map(campaign =>
        updateCampaignStatus(campaign, 'archived')
      );

      await Promise.all(promises);
      toast.success(`Event "${event.name}" and all campaigns archived`);

      // Refresh both data sources so UI updates immediately
      fetch(scopedUrl("/facebook_ads?include_details=true"))
        .then(res => res.json())
        .then(serverData => {
          const processedData = processAndGroupPosts(serverData);
          setPostsData(processedData);
        })
        .catch(err => console.error('Error fetching ads:', err));
      refreshCampaignsFromApi();

      // If we were viewing this event, go back to events view
      if (selectedEvent?.id === event.id) {
        setSelectedEvent(null);
        setViewMode('events');
      }
    } catch (error) {
      console.error('Error archiving event:', error);
      toast.error('Failed to archive some campaigns');
    }
  };

  // Restore archived event (unarchives all campaigns)
  const handleRestoreEvent = async (event) => {
    const confirmed = window.confirm(
      `Restore "${event.name}"? This will restore all ${event.campaigns.length} campaigns for this event to draft status.`
    );

    if (!confirmed) return;

    try {
      // Restore all campaigns for this event to draft
      const promises = event.campaigns.map(campaign =>
        updateCampaignStatus(campaign, 'draft')
      );

      await Promise.all(promises);
      toast.success(`Event "${event.name}" and all campaigns restored`);

      // Refresh both data sources so UI updates immediately
      fetch(scopedUrl("/facebook_ads?include_details=true"))
        .then(res => res.json())
        .then(serverData => {
          const processedData = processAndGroupPosts(serverData);
          setPostsData(processedData);
        })
        .catch(err => console.error('Error fetching ads:', err));
      refreshCampaignsFromApi();
    } catch (error) {
      console.error('Error restoring event:', error);
      toast.error('Failed to restore some campaigns');
    }
  };

  // Event navigation handlers
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setViewMode('campaigns');
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
    setViewMode('events');
  };

  const handleDeleteCampaign = async (campaign) => {
    // Get the database campaign ID
    const campaignId = campaign._campaign_db_id || campaign.id;
    
    if (!campaignId) {
      toast.error('Campaign ID not found');
      return;
    }

    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete the campaign "${campaign.campaign_name}"? This will also delete it from Facebook. This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(scopedUrl(`/api/facebook/campaigns/${campaignId}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Campaign deleted successfully');

        // Refresh both data sources so UI updates immediately
        fetch(scopedUrl("/facebook_ads?include_details=true"))
          .then(res => res.json())
          .then(serverData => {
            const processedData = processAndGroupPosts(serverData);
            setPostsData(processedData);
          })
          .catch(err => console.error('Error fetching ads:', err));
        refreshCampaignsFromApi();
      } else {
        toast.error(data.error || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error(`Error: ${error.message || 'Failed to delete campaign'}`);
    }
  };

  // Transform campaign data from CampaignReview to payload format
  const transformCampaignToPayload = (campaign) => {
    // If campaign already has fullResponse (from AI), use it directly
    if (campaign?.fullResponse) {
      return campaign.fullResponse;
    }

    // Otherwise, build payload from campaign object structure
    const payload = {
      event_data: campaign?.event_data || {
        event_id: campaign?.event_id?.toString(),
        name: campaign?.event_name,
        start_date: campaign?.scheduled_start_at,
        venue_name: campaign?.venue_name
      },
      campaign: {
        campaign_name: {
          value: campaign?.campaign_name || campaign?.name,
          confidence: 'high',
          assumptions: [],
          reason: 'User-saved campaign'
        },
        campaign_objective: {
          value: campaign?.objective || 'Conversions',
          confidence: 'high',
          assumptions: [],
          reason: 'User-selected objective'
        },
        campaign_start_at: {
          value: campaign?.scheduled_start_at || campaign?.start_date,
          confidence: 'high',
          assumptions: [],
          reason: 'User-specified start date'
        },
        campaign_end_at: {
          value: campaign?.scheduled_end_at || campaign?.end_date,
          confidence: 'high',
          assumptions: [],
          reason: 'User-specified end date'
        },
        campaign_buying_type: {
          value: campaign?.buying_type || 'auction',
          confidence: 'high',
          assumptions: [],
          reason: 'Default Meta buying type'
        },
        campaign_special_ad_category: {
          value: campaign?.special_ad_categories?.[0]?.toLowerCase() || 'none',
          confidence: 'high',
          assumptions: [],
          reason: 'User-specified category'
        },
        campaign_strategy_summary: {
          value: campaign?.strategy_summary || '',
          confidence: 'medium',
          assumptions: [],
          reason: 'User-provided strategy'
        },
        campaign_status: campaign?.status || 'draft',
        ad_set: {
          ad_set_name: {
            value: campaign?.ad_set_name || campaign?.campaign_name || campaign?.name,
            confidence: 'high',
            assumptions: [],
            reason: 'Mirrors campaign name'
          },
          billing_event: {
            value: campaign?.billing_event || 'impressions',
            confidence: 'high',
            assumptions: [],
            reason: 'Default billing event'
          },
          optimization_goal: {
            value: campaign?.optimization_goal || 'offsite_conversions',
            confidence: 'high',
            assumptions: [],
            reason: 'User-specified optimization goal'
          },
          bid_strategy: {
            value: campaign?.bid_strategy || 'lowest_cost',
            confidence: 'high',
            assumptions: [],
            reason: 'Default bid strategy'
          },
          budget: {
            type: {
              value: campaign?.budget_strategy === 'lifetime_budget' ? 'lifetime' : 'daily',
              confidence: 'high',
              assumptions: [],
              reason: 'User-specified budget type'
            },
            total_amount: {
              value: campaign?.lifetime_budget || campaign?.budget,
              confidence: 'medium',
              assumptions: [],
              reason: 'User-specified budget'
            },
            daily_amount: {
              value: campaign?.daily_budget,
              confidence: 'medium',
              assumptions: [],
              reason: 'User-specified daily budget'
            }
          },
          schedule: {
            start_date: {
              value: campaign?.scheduled_start_at || campaign?.start_date,
              confidence: 'high',
              assumptions: [],
              reason: 'User-specified start date'
            },
            end_date: {
              value: campaign?.scheduled_end_at || campaign?.end_date,
              confidence: 'high',
              assumptions: [],
              reason: 'User-specified end date'
            }
          },
          age: {
            min: {
              value: campaign?.targeting?.age_min || campaign?.age_min || 18,
              confidence: 'medium',
              assumptions: [],
              reason: 'User-specified age targeting'
            },
            max: {
              value: campaign?.targeting?.age_max || campaign?.age_max || 65,
              confidence: 'medium',
              assumptions: [],
              reason: 'User-specified age targeting'
            }
          },
          location: {
            value: campaign?.targeting?.location_string || campaign?.location || 'US',
            confidence: 'medium',
            assumptions: [],
            reason: 'User-specified location'
          },
          ad_set_status: campaign?.ad_set_status || 'draft',
          ad_creative: {
            ad_creative_name: {
              value: campaign?.creative_name || campaign?.name,
              confidence: 'high',
              assumptions: [],
              reason: 'User-specified creative name'
            },
            ad_creative_cta_type: {
              value: campaign?.cta_type || 'BUY_NOW',
              confidence: 'high',
              assumptions: [],
              reason: 'User-specified CTA'
            },
            ad_creative_link: {
              value: campaign?.link || campaign?.page_id || 'page_id',
              confidence: 'medium',
              assumptions: [],
              reason: 'User-specified link'
            },
            ad_creative_primary_text: {
              value: Array.isArray(campaign?.message) ? campaign.message : [campaign?.message || ''],
              confidence: 'high',
              assumptions: [],
              reason: 'User-provided message'
            },
            ad_creative_headline: {
              value: Array.isArray(campaign?.headline) ? campaign.headline : [campaign?.headline || ''],
              confidence: 'high',
              assumptions: [],
              reason: 'User-provided headline'
            },
            ad_creative_description: {
              value: Array.isArray(campaign?.description) ? campaign.description : [campaign?.description || ''],
              confidence: 'high',
              assumptions: [],
              reason: 'User-provided description'
            },
            ad_creative_policy_safety_notes: {
              value: campaign?.policy_safety_notes || '',
              confidence: 'medium',
              assumptions: [],
              reason: 'User-provided notes'
            },
            ad_creative_notes: {
              value: campaign?.notes || '',
              confidence: 'medium',
              assumptions: [],
              reason: 'User-provided notes'
            },
            ad_name: {
              value: campaign?.ad_name || campaign?.name,
              confidence: 'high',
              assumptions: [],
              reason: 'User-specified ad name'
            }
          }
        }
      },
      analytics_metadata: campaign?.analytics_metadata || campaign?.analysis_metadata?.analytics_metadata || {}
    };

    return payload;
  };

  const handleSaveCampaign = async (campaign, action = "update") => {
    try {
      // If action is "schedule", just schedule existing ads without saving
      if (action === "schedule") {
        const campaignId = campaign?.id || campaign?._campaign_db_id;
        
        if (!campaignId) {
          toast.error('Campaign ID not found');
          return;
        }
        
        // Fetch ads for this campaign using campaign_id parameter
        const adsResponse = await fetch(scopedUrl(`/facebook_ads?campaign_id=${campaignId}&include_details=true`), {
          headers: { 'Accept': 'application/json' }
        });
        
        if (adsResponse.ok) {
          const adsData = await adsResponse.json();
          // Flatten grouped data (grouped by date) into a single array
          const campaignAds = Object.values(adsData).flat();
          
          if (campaignAds.length === 0) {
            toast.error('No ads found for this campaign');
            return;
          }
          
          // Schedule and approve each ad
          const schedulePromises = campaignAds.map(ad => {
            if (ad.status !== 'published' && ad.status !== 'scheduled') {
              return fetch(scopedUrl(`/facebook_ads/${ad.id}/schedule_and_approve`), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                body: JSON.stringify({
                  id: ad.id,
                  status: 'scheduled'
                })
              }).catch(err => {
                console.error(`Error scheduling ad ${ad.id}:`, err);
                return null;
              });
            }
            return Promise.resolve();
          });
          
          await Promise.all(schedulePromises);
          toast.success('Campaign approved and scheduled successfully!');
          
          // Refresh ads data
          fetch(scopedUrl("/facebook_ads?include_details=true"))
            .then(res => res.json())
            .then(serverData => {
              const processedData = processAndGroupPosts(serverData);
              setPostsData(processedData);
            })
            .catch(err => console.error('Error fetching ads:', err));
          
          handleCloseReview();
          
          // Reload the page to show updated status
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          toast.error('Failed to fetch ads for campaign');
        }
        return;
      }
      
      // Otherwise, save the campaign normally
      const payload = transformCampaignToPayload(campaign);
      const eventId = campaign?.event_id || campaign?.event_data?.event_id || null;

      const response = await fetch(scopedUrl('/api/facebook/campaigns/populate_from_payload'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          payload: payload,
          event_id: eventId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Campaign saved successfully!');
        
        // Refresh ads data
        fetch(scopedUrl("/facebook_ads?include_details=true"))
          .then(res => res.json())
          .then(serverData => {
            const processedData = processAndGroupPosts(serverData);
            setPostsData(processedData);
          })
          .catch(err => console.error('Error fetching ads:', err));
        
        handleCloseReview();
      } else {
        toast.error(data.error || data.errors?.join(', ') || 'Failed to save campaign');
        console.error('Save campaign error:', data);
      }
    } catch (error) {
      toast.error('Error saving campaign: ' + error.message);
      console.error('Save campaign error:', error);
    }
  };

  const EventCardList = ({ campaigns, onEditCampaign, onApproveCampaign, onRejectCampaign, onArchiveCampaign, onDeleteCampaign }) => {
    if (campaigns.length === 0) {
      return (
        <div className="meta-ads-ai-page__card-list-empty">
          <p>No campaigns found. Create your first campaign with AI!</p>
        </div>
      );
    }

    // Helper to determine badge class based on campaign status
    const getBadgeClass = (status) => {
      if (status === 'draft') return 'meta-ads-ai-page__card-badge--draft';
      if (status === 'archived') return 'meta-ads-ai-page__card-badge--archived';
      if (status === 'paused') return 'meta-ads-ai-page__card-badge--paused';
      if (status === 'active') return 'meta-ads-ai-page__card-badge--active';
      if (status === 'scheduled') return 'meta-ads-ai-page__card-badge--scheduled';
      if (status === 'published') return 'meta-ads-ai-page__card-badge--published';
      return 'meta-ads-ai-page__card-badge--active';
    };

    const getBadgeLabel = (campaign) => {
      return ((campaign.status || 'draft') + '').toUpperCase();
    };

    // Render context-sensitive action button
    const renderActionButton = (campaign) => {
      if (campaign.status === 'draft') {
        // Draft campaigns get Approve + Reject buttons
        return (
          <>
            <button
              type="button"
              className="meta-ads-ai-page__card-btn-approve"
              onClick={(e) => {
                e.stopPropagation();
                if (onApproveCampaign) {
                  onApproveCampaign(campaign);
                }
              }}
              title="Approve and push to Meta Ads Manager"
            >
              Approve
            </button>
            <button
              type="button"
              className="meta-ads-ai-page__card-btn-reject"
              onClick={(e) => {
                e.stopPropagation();
                if (onRejectCampaign) {
                  onRejectCampaign(campaign);
                }
              }}
              title="Rejected campaigns will be archived and moved to your Archived tab"
            >
              Reject
            </button>
          </>
        );
      } else if (campaign.status === 'archived') {
        // Archived campaigns - no action button, just view
        return null;
      } else {
        // Approved campaigns (active, paused, scheduled, published) get Archive button
        return (
          <button
            type="button"
            className="meta-ads-ai-page__card-btn-archive"
            onClick={(e) => {
              e.stopPropagation();
              if (onArchiveCampaign) {
                onArchiveCampaign(campaign);
              }
            }}
            title="Archive this campaign"
          >
            Archive
          </button>
        );
      }
    };

    return (
      <div className="meta-ads-ai-page__card-grid">
        {campaigns.map((campaign) => (
          <div
            key={campaign.id}
            className="meta-ads-ai-page__card"
            onClick={() => onEditCampaign(campaign)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onEditCampaign(campaign)}
          >
            <div className="meta-ads-ai-page__card-row">
              <div>
                <h3 className="meta-ads-ai-page__card-title">
                  {campaign.campaign_name}
                </h3>
                <p className="meta-ads-ai-page__card-event">
                  {campaign.display_event_name}
                </p>
                <div className="meta-ads-ai-page__card-meta">
                  <span className={`meta-ads-ai-page__card-badge ${getBadgeClass(campaign.status)}`}>
                    {getBadgeLabel(campaign)}
                  </span>
                  <span className="meta-ads-ai-page__card-ads-count">
                    {campaign.ads?.length || 0} ads
                  </span>
                </div>
              </div>
              <div className="meta-ads-ai-page__card-actions">
                <button
                  type="button"
                  className="meta-ads-ai-page__card-btn-view"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditCampaign(campaign);
                  }}
                >
                  View
                </button>
                {renderActionButton(campaign)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Event card component for event-first navigation - horizontal row layout
  const EventCardGrid = ({ events, onSelectEvent, onArchiveEvent, onRestoreEvent, isArchivedView = false }) => {
    if (events.length === 0) {
      return (
        <div className="meta-ads-ai-page__card-list-empty">
          <p>{isArchivedView ? 'No archived events found.' : 'No events found. Create your first campaign with AI!'}</p>
        </div>
      );
    }

    return (
      <div className="meta-ads-ai-page__event-list">
        {events.map((event) => (
          <div
            key={event.id}
            className={`meta-ads-ai-page__event-row ${isArchivedView ? 'meta-ads-ai-page__event-row--archived' : ''}`}
            onClick={() => onSelectEvent(event)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onSelectEvent(event)}
          >
            {/* Left: Event name */}
            <div className="meta-ads-ai-page__event-row-name">
              <h3>{event.name}</h3>
            </div>

            {/* Middle: Status badges (actual status: active, paused, scheduled, published, draft) */}
            <div className="meta-ads-ai-page__event-row-status">
              {event.activeCampaigns > 0 && (
                <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--active">
                  {event.activeCampaigns} active
                </span>
              )}
              {event.pausedCampaigns > 0 && (
                <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--paused">
                  {event.pausedCampaigns} paused
                </span>
              )}
              {event.scheduledCampaigns > 0 && (
                <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--scheduled">
                  {event.scheduledCampaigns} scheduled
                </span>
              )}
              {event.publishedCampaigns > 0 && (
                <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--published">
                  {event.publishedCampaigns} published
                </span>
              )}
              {event.draftCampaigns > 0 && (
                <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--draft">
                  {event.draftCampaigns} draft
                </span>
              )}
              {event.activeCampaigns === 0 && event.pausedCampaigns === 0 && event.scheduledCampaigns === 0 && event.publishedCampaigns === 0 && event.draftCampaigns === 0 && (
                <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--archived">
                  No campaigns
                </span>
              )}
            </div>

            {/* Right: Total + actions */}
            <div className="meta-ads-ai-page__event-row-actions">
              <span className="meta-ads-ai-page__event-row-total">
                {event.campaigns.length} campaign{event.campaigns.length !== 1 ? 's' : ''}
              </span>
              {/* No bulk archive on the shared bucket: it holds unrelated live campaigns, and
                  archiving a published campaign archives it on Meta too. Archive them one by one. */}
              {event.isCompanyLevel && !isArchivedView ? null : !isArchivedView ? (
                <button
                  type="button"
                  className="meta-ads-ai-page__event-btn-archive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchiveEvent(event);
                  }}
                  title="Archive all campaigns for this event"
                >
                  Archive
                </button>
              ) : (
                <button
                  type="button"
                  className="meta-ads-ai-page__event-btn-restore"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestoreEvent(event);
                  }}
                  title="Restore this event"
                >
                  Restore
                </button>
              )}
              <span className="meta-ads-ai-page__event-row-view">View →</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const EmptyState = () => {
    return (
      <div className="meta-ads-ai-page__empty">
        <h3 className="meta-ads-ai-page__empty-title">
          No campaigns yet
        </h3>
        <p className="meta-ads-ai-page__empty-text">
          Use the "Draft with AI" button to create your first campaign
        </p>
      </div>
    );
  };

  return (
    <div className={`social-media-ai-page meta-ads-ai-page ${connectModalOpen ? 'blurred' : ''}`}>
      <EditAdModal
        isOpen={modalOpen}
        post={currentPost}
        onClose={() => {
          closeModal();
        }}
        onSave={savePostChanges}
        onDelete={handleDeleteAd}
        companySlug={companySlug}
      />

      {/* Connect Page Modal */}
      <ConnectPageModal
        isOpen={connectModalOpen}
        onClose={() => {
          // Allow closing via skip button (for AI Draft testing)
          setConnectModalOpen(false);
        }}
      />

      {/* Settings Modal */}
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} isAdmin={isAdmin} companySlug={companySlug} />

      {/* Queue + Reports Modal */}
      <ReportsList isOpen={queueModalOpen} onClose={() => setQueueModalOpen(false)} isAdmin={isAdmin} companySlug={companySlug} />

      {/* Generation Queue Panel */}
      <GenerationQueuePanel isAdmin={isAdmin} companySlug={companySlug} onComplete={() => {
        setIsGenerating(false);
        sessionStorage.removeItem('metaAdsAI_generating');
        sessionStorage.removeItem('metaAdsAI_generation_start');
        sessionStorage.removeItem('metaAdsAI_report_id');
      }} />

      <div className="meta-ads-dashboard-page">
        <div className="container-fluid px-4">
          {/* Breadcrumb above Meta Ads Dashboard */}
          <nav className="meta-ads-ai-page__breadcrumb" aria-label="Breadcrumb">
            <span className="meta-ads-ai-page__breadcrumb-items">
              {viewMode === 'campaigns' ? (
                <button type="button" className="meta-ads-ai-page__breadcrumb-link meta-ads-ai-page__breadcrumb-btn" onClick={handleBackToEvents}>
                  Events
                </button>
              ) : (
                <span className="meta-ads-ai-page__breadcrumb-text meta-ads-ai-page__breadcrumb-current">Events</span>
              )}
              {viewMode === 'campaigns' && selectedEvent && (
                <>
                  <span className="meta-ads-ai-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  <span className="meta-ads-ai-page__breadcrumb-text meta-ads-ai-page__breadcrumb-current" title={selectedEvent.name}>
                    {selectedEvent.name.length > 50 ? selectedEvent.name.slice(0, 50) + '...' : selectedEvent.name}
                  </span>
                  <span className="meta-ads-ai-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  <span className="meta-ads-ai-page__breadcrumb-text">Campaigns</span>
                </>
              )}
            </span>
            {isAdmin && (
              <span className="admin-only-legend meta-ads-ai-page__breadcrumb-legend">
                <span className="admin-only-dot" />
                = Admin only
              </span>
            )}
          </nav>

          {/* Header */}
          <div className="meta-ads-dashboard__header meta-ads-ai-page__header-wrap">
            <div className="meta-ads-dashboard__title-row meta-ads-ai-page__title-row">
              <h1 className="meta-ads-dashboard__title meta-ads-ai-page__page-title">
                Meta Ads Dashboard
              </h1>
              <div className="meta-ads-dashboard__header-actions meta-ads-ai-page__header-actions">
                {/* Sync from Meta button */}
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleSyncCampaigns}
                  disabled={syncing}
                  title={lastSyncedAt ? `Last synced: ${new Date(lastSyncedAt).toLocaleString()}` : 'Sync campaigns from Meta Ads Manager'}
                >
                  {syncing ? 'Syncing...' : 'Sync from Meta'}
                </button>
                {lastSyncedAt && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', alignSelf: 'center' }}>
                    Synced {new Date(lastSyncedAt).toLocaleTimeString()}
                  </span>
                )}
                {/* Draft with AI button */}
                <button type="button" className="meta-ads-ai-page__btn-ai-draft" onClick={handleOpenAIDraft}>
                  Draft with AI
                </button>
                {/* Queue / Reports button */}
                <button type="button" className="meta-ads-ai-page__btn-queue" onClick={() => isAdmin ? navigate('/marketing/meta_ads_ai/queue') : setQueueModalOpen(true)} title="AI Generation Queue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button type="button" className="meta-ads-ai-page__btn-settings settings-btn" onClick={() => setSettingsOpen(true)} title="Settings">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Status Filter Tabs - only show in campaigns view; counts as badges on tabs */}
            {viewMode === 'campaigns' && (
              <div className="meta-ads-ai-page__filters-wrap">
                {STATUS_FILTERS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`meta-ads-ai-page__filter-btn ${statusFilter === value ? 'meta-ads-ai-page__filter-btn--active' : ''}`}
                    onClick={() => handleFilterStatus(value)}
                  >
                    <span className="meta-ads-ai-page__filter-label">{label}</span>
                    <span className="meta-ads-ai-page__filter-count">({statusCounts[value] ?? 0})</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Admin-only: Event AI Usage Info Bar */}
          {isAdmin && viewMode === 'campaigns' && eventTokenSummary && (
            <div className="meta-ads-ai-page__token-info-bar">
              <span className="meta-ads-ai-page__token-info-text">
                AI Usage<span className="admin-only-dot" />: {eventTokenSummary.total_tokens.toLocaleString()} tokens
                {' '}&bull;{' '}
                ~${eventTokenSummary.estimated_cost < 0.01 ? '< 0.01' : eventTokenSummary.estimated_cost.toFixed(2)}
              </span>
            </div>
          )}

          {/* Content */}
          {viewMode === 'events' ? (
            /* Events View — gate on both fetches to prevent pop-in */
            (!adsLoaded || !campaignsLoaded) ? (
              <div className="meta-ads-dashboard__content" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                Loading campaigns...
              </div>
            ) : (
            <div className="meta-ads-dashboard__content">
              {/* Events Header with Archived Toggle */}
              <div className="meta-ads-ai-page__events-header">
                {!showArchivedEvents ? (
                  <>
                    <div className="meta-ads-ai-page__events-header-left">
                      <h2 className="meta-ads-ai-page__events-title">Your Events</h2>
                      <span className="meta-ads-ai-page__events-count">
                        {activeEvents.length} event{activeEvents.length !== 1 ? 's' : ''} • {convertAdsToCampaigns.length} campaign{convertAdsToCampaigns.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {archivedEvents.length > 0 && (
                      <button
                        type="button"
                        className="meta-ads-ai-page__archived-toggle"
                        onClick={() => setShowArchivedEvents(true)}
                      >
                        View Archived Events →
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="meta-ads-ai-page__back-link"
                      onClick={() => setShowArchivedEvents(false)}
                    >
                      ← Back to Active Events
                    </button>
                    <h2 className="meta-ads-ai-page__events-title">Archived Events</h2>
                  </>
                )}
              </div>

              {/* Event Cards */}
              {!showArchivedEvents ? (
                activeEvents.length > 0 ? (
                  <EventCardGrid
                    events={activeEvents}
                    onSelectEvent={handleSelectEvent}
                    onArchiveEvent={handleArchiveEvent}
                    onRestoreEvent={handleRestoreEvent}
                  />
                ) : (
                  <EmptyState />
                )
              ) : (
                <EventCardGrid
                  events={archivedEvents}
                  onSelectEvent={handleSelectEvent}
                  onArchiveEvent={handleArchiveEvent}
                  onRestoreEvent={handleRestoreEvent}
                  isArchivedView={true}
                />
              )}
            </div>
            )
          ) : (
            /* Campaigns View (for selected event) */
            <div className="meta-ads-dashboard__content">
              {/* Campaign Cards */}
              <div className="row">
                <div className="col-12">
                  {campaignsForSelectedEvent.length > 0 ? (
                    <EventCardList
                      campaigns={campaignsForSelectedEvent}
                      onEditCampaign={handleEditCampaign}
                      onApproveCampaign={handleApproveCampaign}
                      onRejectCampaign={handleRejectCampaign}
                      onArchiveCampaign={handleArchiveCampaign}
                      onDeleteCampaign={handleDeleteCampaign}
                    />
                  ) : (
                    <div className="meta-ads-ai-page__card-list-empty">
                      <p>No campaigns match the current filter.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Campaign Review Modal */}
      {selectedCampaign && (
        <CampaignReview
          campaign={selectedCampaign}
          isOpen={isReviewOpen}
          onClose={handleCloseReview}
          onApprove={handleApproveCampaign}
          onReject={handleRejectCampaign}
          onArchive={handleArchiveCampaign}
          onSave={handleSaveCampaign}
          onRefetchCampaign={async () => {
            if (!selectedCampaign?.id) return;
            try {
              const res = await fetch(scopedUrl(`/api/facebook/campaigns/${selectedCampaign.id}`), { headers: { Accept: 'application/json' } });
              const data = await res.json();
              if (res.ok && data) setSelectedCampaign(data);
            } catch (e) {
              console.error('Failed to refetch campaign:', e);
            }
          }}
          companySlug={companySlug}
        />
      )}

      {/* Generate AI Modal */}
      <GenerateAIModal
        isOpen={isAIDraftOpen}
        onClose={handleCloseAIDraft}
        onGenerate={(data) => {
          setIsGenerating(true);
          handleAIDraftSuccess(data);
        }}
        companySlug={companySlug}
      />

    </div>
  )
}

export default MetaAdsAIPage
